import { type Dispatch, type SetStateAction, useCallback, useState } from "react";

export type PeriodSelection<TId> = {
	selectedIds: Set<TId>;
	setSelectedIds: Dispatch<SetStateAction<Set<TId>>>;
	toggle: (id: TId) => void;
	isSelected: (id: TId) => boolean;
	selectedCount: number;
};

const sameList = <TId>(left: readonly TId[], right: readonly TId[]): boolean =>
	left.length === right.length && left.every((id, index) => id === right[index]);

const sameIds = <TId>(left: ReadonlySet<TId>, right: ReadonlySet<TId>): boolean => {
	if (left.size !== right.size) return false;
	for (const id of left) {
		if (!right.has(id)) return false;
	}
	return true;
};

const idsInList = <TId>(source: Iterable<TId>, allowed: ReadonlySet<TId>): Set<TId> =>
	new Set([...source].filter((id) => allowed.has(id)));

/**
 * Checked set for a period list.
 *
 * The checked set starts as `defaultIds`. It is replaced with `defaultIds` only when the list
 * goes from empty to populated. An id that shows up later stays unchecked, an id the caller
 * clears stays clear while it is still listed, and an id that leaves the list is dropped.
 */
export const usePeriodSelection = <TId>(
	itemIds: readonly TId[],
	defaultIds: ReadonlySet<TId> | readonly TId[],
): PeriodSelection<TId> => {
	const [selectedIds, setSelectedIds] = useState(() => idsInList(defaultIds, new Set(itemIds)));
	const [trackedItemIds, setTrackedItemIds] = useState(itemIds);

	if (!sameList(itemIds, trackedItemIds)) {
		const itemIdSet = new Set(itemIds);
		const shouldSeed = trackedItemIds.length === 0 && itemIds.length > 0;
		setTrackedItemIds(itemIds);
		setSelectedIds((prev) => {
			const next = idsInList(shouldSeed ? defaultIds : prev, itemIdSet);
			return sameIds(prev, next) ? prev : next;
		});
	}

	const toggle = useCallback((id: TId) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const isSelected = useCallback((id: TId): boolean => selectedIds.has(id), [selectedIds]);

	return {
		selectedIds,
		setSelectedIds,
		toggle,
		isSelected,
		selectedCount: selectedIds.size,
	};
};
